import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ArrowLeft } from 'lucide-react';
import { api } from '@/lib/api';
import { BlockEditor } from '@/components/BlockEditor';
import type { Lesson, Block } from '@/types';

export function CourseEditorPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const queryClient = useQueryClient();
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);

  const { data: courseData } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => api.getCourse(courseId!),
    enabled: !!courseId,
  });

  const course = (courseData as any)?.course;
  const lessons = ((courseData as any)?.lessons || []) as Lesson[];
  const selectedLesson = lessons.find((l) => l.id === selectedLessonId);

  const updateLessonMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.updateLesson(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    },
  });

  const createLessonMutation = useMutation({
    mutationFn: (data: any) => api.createLesson(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course', courseId] });
    },
  });

  const handleCreateLesson = async () => {
    const result = await createLessonMutation.mutateAsync({
      courseId,
      title: 'New Lesson',
      blocks: [],
    });

    if ((result as any).lesson) {
      setSelectedLessonId((result as any).lesson.id);
    }
  };

  const handleUpdateBlocks = (blocks: Block[]) => {
    if (selectedLessonId) {
      updateLessonMutation.mutate({
        id: selectedLessonId,
        data: { blocks },
      });
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)]">
      {/* Sidebar - Lessons List */}
      <div className="w-64 border-r border-gray-200 bg-white overflow-y-auto">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <ArrowLeft className="w-4 h-4" />
            <h2 className="font-semibold">{course?.title || 'Course'}</h2>
          </div>

          <button
            onClick={handleCreateLesson}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 transition-colors mb-4"
          >
            <Plus className="w-4 h-4" />
            Add Lesson
          </button>

          <div className="space-y-2">
            {lessons.map((lesson, index) => (
              <button
                key={lesson.id}
                onClick={() => setSelectedLessonId(lesson.id)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  selectedLessonId === lesson.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="text-xs text-gray-500 mb-1">
                  Lesson {index + 1}
                </div>
                <div className="font-medium text-sm">{lesson.title}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Editor */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {selectedLesson ? (
          <>
            <div className="border-b border-gray-200 bg-white px-6 py-4">
              <input
                type="text"
                value={selectedLesson.title}
                onChange={(e) =>
                  updateLessonMutation.mutate({
                    id: selectedLesson.id,
                    data: { title: e.target.value },
                  })
                }
                className="text-2xl font-bold w-full border-none focus:outline-none"
                placeholder="Lesson Title"
              />
            </div>

            <div className="flex-1 overflow-y-auto bg-gray-50">
              <BlockEditor
                blocks={selectedLesson.blocks}
                onChange={handleUpdateBlocks}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <p className="mb-4">Select a lesson to start editing</p>
              <button
                onClick={handleCreateLesson}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Create First Lesson
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
